<script setup>
import { onMounted, ref } from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js';

const notificationStore = useNotificationStore();

const props = defineProps({
    companyId: {
        type: Number,
        required: true
    }
})

const appData = ref(null);
const creatingApp = ref(false);
const createAppName = ref('');
const launchFeeOrderId = ref(null);

const loadData = async () => {
    const res = await axios.get(route('admin.company.whitelabelApp.dashboard', [props.companyId]));
    appData.value = res.data;
};

const createApp = async () => {
    await axios.post(route('admin.company.whitelabelApp.store', [props.companyId]), {
        name: createAppName.value,
    });
    await loadData();
};

const updateAppData = async () => {
    await axios.put(route('admin.company.whitelabelApp.update', [props.companyId, appData.value.app_id]), appData.value);

    notificationStore.addNotification({
        type: 'success',
        title: 'App data updated',
        dismissAfter: 3000,
    });
};

const chargeLaunchFee = async (orderId) => {
    if (! orderId || launchFeeOrderId.value) {
        return;
    }

    launchFeeOrderId.value = orderId;

    try {
        await axios.post(
            route('admin.company.whitelabelApp.chargeLaunchFee', [props.companyId, orderId]),
        );
        notificationStore.addNotification({
            type: 'success',
            title: 'Launch fee charged',
            dismissAfter: 3000,
        });
        await loadData();
    } catch (error) {
        console.error(error);
        notificationStore.addNotification({
            type: 'error',
            title: error.response?.data?.error ?? 'Failed to charge launch fee',
            dismissAfter: 5000,
        });
    } finally {
        launchFeeOrderId.value = null;
    }
};

const formatCurrency = (amountCents, currency = 'eur') => {
    if (amountCents == null) {
        return '';
    }

    return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currency?.toUpperCase() ?? 'EUR',
        minimumFractionDigits: 0,
    }).format(amountCents / 100);
};

onMounted(() => {
    loadData();
});

</script>
<template>
    <div v-if="appData">
        <card-basic v-if="! appData.app_exists">
            <h2 class="mb-2">Whitelabel App</h2>
            <div
                v-if="! creatingApp"
                class="flex flex-col items-center mt-2"
            >
                <h3 class="my-4">Whitelabel App not enabled for this company</h3>
                <button-primary
                    @click="creatingApp = true"
                >Create whitelabel app
                </button-primary>
            </div>
            <div
                v-if="creatingApp"
                class="flex flex-col items-center"
            >
                <h3>Creating whitelabel app</h3>
                <div class="flex items-center space-x-4">
                    <input-text
                        v-model:content="createAppName"
                        autofocus
                        class="!mb-0"
                    ></input-text>
                    <button-success
                        @click="createApp"
                    >
                        Create app
                    </button-success>
                </div>
            </div>
        </card-basic>

        <card-basic v-if="appData.app_exists">
            <h2>Whitelabel App</h2>
            <p v-if="appData.lead">
                <a class="mf-link" :href="route('crm.leads.view', appData.lead.id)">
                    <i class="fa-regular fa-user text-gray-400 mr-1"></i>
                    {{ appData.lead.name }}
                </a>
            </p>
            <hr class="mb-4 mt-4">
            <div class="grid grid-cols-2 gap-4">
                <div>
                    <toggle-switch
                        label="App Enabled"
                        v-model:content="appData.app_enabled"
                        class="mt-1"
                    />
                    <toggle-switch
                        label="Using Runway"
                        v-model:content="appData.using_runway"
                        class="mt-1"
                    />
                    <toggle-switch
                        label="App Published"
                        v-model:content="appData.app_published"
                        class="mt-1"
                    />
                </div>
                <input-text
                    label="App name (internal)"
                    v-model:content="appData.app_name"
                />

                <hr class="col-span-2">

                <div class="space-y-4">
                    <h2>Apple</h2>
                    <div>
                        <input-label label="Apple developer account created at" />
                        <input type="date"
                            v-model="appData.app_listing.apple_developer_account_created_at"
                               class="border-gray-400 border w-full rounded-md p-2"
                        />
                    </div>

                    <div>
                        <input-label label="iOS app created at" />
                        <input type="date"
                            v-model="appData.app_listing.apple_app_created_at"
                               class="border-gray-400 border w-full rounded-md p-2"
                        />
                    </div>

                    <div>
                        <input-label label="Published iOS app version number" />
                        <input-text
                            v-model:content="appData.app_listing.apple_published_app_version"
                        />
                    </div>
                </div>

                <div class="space-y-4">
                    <h2>Google</h2>
                    <div>
                        <input-label label="Google developer account created at" />
                        <input type="date"
                               v-model="appData.app_listing.google_developer_account_created_at"
                               class="border-gray-400 border w-full rounded-md p-2"
                        />
                    </div>

                    <div>
                        <input-label label="Android app created at" />
                        <input type="date"
                               v-model="appData.app_listing.google_app_created_at"
                               class="border-gray-400 border w-full rounded-md p-2"
                        />
                    </div>

                    <div>
                        <input-label label="Published Android app version number" />
                        <input-text
                            v-model:content="appData.app_listing.google_published_app_version"
                        />
                    </div>
                </div>
                <hr class="col-span-2">
            </div>

            <h2 class="mt-4 mb-2">Listing information</h2>

            <div class="space-y-6">
                <div>
                    <input-label label="Name" />
                    <input-text
                        v-model:content="appData.app_listing.app_listing_name"
                    >{{ appData.app_listing.app_listing_name }}</input-text>
                </div>
                <div>
                    <input-label label="Byline" />
                    <input-text
                        v-model:content="appData.app_listing.app_listing_byline"
                    >{{ appData.app_listing.app_listing_byline }}</input-text>
                </div>
                <div>
                    <input-label label="Description" />
                    <input-textarea
                        v-model:content="appData.app_listing.app_listing_description"
                    >{{ appData.app_listing.app_listing_description }}</input-textarea>
                </div>
                <div>
                    <input-label label="Keywords" />
                    <input-text
                        v-model:content="appData.app_listing.app_listing_keywords"
                    >{{ appData.app_listing.app_listing_keywords }}</input-text>
                </div>
                <div>
                    <input-label label="Support_email" />
                    <input-text
                        v-model:content="appData.app_listing.app_listing_support_email"
                    >{{ appData.app_listing.app_listing_support_email }}</input-text>
                </div>
            </div>

            <h2 class="mt-4 mb-2">Test Flight</h2>

            <div>
                <ul class="space-y-2">
                    <li
                        v-for="(testpilot, i) in appData.testpilots"
                        :key="i"
                    >
                        <div class="flex justify-between items-center space-x-4">
                                <span>
                                    {{ testpilot.name }}
                                </span>
                            <span class="text-gray-400 text-xs">
                                    {{ testpilot.created_at }}
                                </span>
                        </div>
                        <div class="text-xxs text-gray-400">
                            Apple: <span class="select-all">{{ testpilot.apple_email ?? '–' }}</span><br>
                            Google: <span class="select-all">{{ testpilot.google_email ?? '–'}}</span>
                        </div>
                    </li>
                </ul>
            </div>

            <div class="flex justify-end">
                <button-success
                    @click="updateAppData"
                >
                    Update
                </button-success>
            </div>
        </card-basic>

        <card-basic v-if="appData.orders && appData.orders.length">
            <h2 class="mb-4">Branded app orders</h2>
            <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                    <thead>
                    <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                        <th class="py-2 pr-4">Date</th>
                        <th class="py-2 pr-4">Plan</th>
                        <th class="py-2 pr-4">Amount</th>
                        <th class="py-2 pr-4">Status</th>
                        <th class="py-2 pr-4">Launch fee</th>
                        <th class="py-2">Actions</th>
                    </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                    <tr v-for="order in appData.orders" :key="order.id">
                        <td class="py-3 pr-4 text-xs text-gray-500">
                            {{ order.purchased_at ?? '—' }}
                        </td>
                        <td class="py-3 pr-4 capitalize">
                            {{ order.plan_type }}
                        </td>
                        <td class="py-3 pr-4 font-semibold text-gray-900">
                            {{ formatCurrency(order.amount_cents, order.currency) }}
                        </td>
                        <td class="py-3 pr-4 capitalize">
                            {{ order.status }}
                        </td>
                        <td class="py-3 pr-4 text-xs">
                            <span v-if="order.launch_fee_charged_at" class="text-emerald-700">
                                Charged {{ order.launch_fee_charged_at }}
                            </span>
                            <span v-else-if="order.requires_launch_fee" class="text-amber-700">
                                Pending launch fee ({{ formatCurrency(order.launch_fee_amount_cents, order.launch_fee_currency) }})
                            </span>
                            <span v-else class="text-gray-500">n/a</span>
                        </td>
                        <td class="py-3">
                            <button
                                v-if="order.can_charge_launch_fee"
                                type="button"
                                class="rounded-md border border-myflowGreen-900 px-3 py-1 text-xs font-semibold text-myflowGreen-900 hover:bg-myflowGreen-50"
                                :disabled="launchFeeOrderId === order.id"
                                @click="chargeLaunchFee(order.id)"
                            >
                                <span v-if="launchFeeOrderId === order.id">Charging…</span>
                                <span v-else>Charge launch fee</span>
                            </button>
                            <span v-else class="text-xs text-gray-400">—</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </card-basic>
    </div>
</template>
